import { Injectable } from '@angular/core';

import { environment } from '../../../environments/environment';
import { resolveApiBaseUrl } from '@core/api/api-base-url';
import { sanitizeRoute } from './sanitize-route';
import { clipLabel, labelFromElement, selectChangeLabel } from './visit-action';

interface VisitEvent {
  kind: 'enter' | 'page' | 'action' | 'search';
  route: string;
  label?: string;
  at: string;
}

const MAX_QUEUE = 80;
const MAX_BATCH = 30;
const FLUSH_MS = 1500;

/**
 * Records arrivals, page changes, clicks, and product search phrases.
 * Other typed fields are not sent.
 */
@Injectable({ providedIn: 'root' })
export class VisitLogService {
  private readonly endpoint = `${resolveApiBaseUrl(
    globalThis.location?.hostname ?? 'localhost',
    environment.apiBaseUrl,
    globalThis.location?.protocol ?? 'http:'
  )}/api/site-visits`;

  private queue: VisitEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listening = false;
  private lastRoute = '';
  private lastActionKey = '';
  private lastActionAt = 0;
  private lastSearchKey = '';
  private lastSearchAt = 0;
  private cachedVisitorId = '';

  private readonly onClick = (event: Event) => {
    const label = labelFromElement(event.target);
    if (!label) return;
    const route = this.currentRoute();
    const key = `${route}\n${label}`;
    const now = Date.now();
    if (key === this.lastActionKey && now - this.lastActionAt < 400) return;
    this.lastActionKey = key;
    this.lastActionAt = now;
    this.push('action', route, label);
  };

  private readonly onChange = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    const label = selectChangeLabel(
      target.getAttribute('aria-label'),
      target.getAttribute('title'),
      target.selectedOptions[0]?.textContent ?? ''
    );
    if (!label) return;
    this.push('action', this.currentRoute(), label);
  };

  /** Hooks clicks and records the arrival. Call once at startup. */
  start(): void {
    if (this.listening || typeof document === 'undefined') return;
    this.listening = true;
    this.push('enter', this.currentRoute());
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('change', this.onChange, true);
    window.addEventListener('pagehide', () => this.flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
    });
  }

  /** Records a product search phrase once it is actually sent. */
  noteSearch(phrase: string): void {
    const label = clipLabel(phrase, 200);
    if (label.length < 2) return;
    const route = this.currentRoute();
    const key = `${route}\n${label}`;
    const now = Date.now();
    if (key === this.lastSearchKey && now - this.lastSearchAt < 800) return;
    this.lastSearchKey = key;
    this.lastSearchAt = now;
    this.push('search', route, label);
  }

  /** Records a page when the router lands on a different path. */
  noteNavigation(url: string): void {
    const route = sanitizeRoute(url);
    if (!route || route === this.lastRoute) return;
    this.push('page', route);
  }

  private visitorId(): string {
    if (this.cachedVisitorId) return this.cachedVisitorId;
    const key = 'commpare_visitor';
    try {
      const existing = localStorage.getItem(key);
      if (existing && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(existing)) {
        this.cachedVisitorId = existing;
        return existing;
      }
      const created = crypto.randomUUID();
      localStorage.setItem(key, created);
      this.cachedVisitorId = created;
      return created;
    } catch {
      this.cachedVisitorId = crypto.randomUUID();
      return this.cachedVisitorId;
    }
  }

  private currentRoute(): string {
    if (typeof location === 'undefined') return '/';
    return sanitizeRoute(`${location.pathname}${location.search}`) || '/';
  }

  private push(kind: VisitEvent['kind'], route: string, label?: string): void {
    this.lastRoute = route;
    const event: VisitEvent = { kind, route, at: new Date().toISOString() };
    if (label) event.label = label;
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
    if (kind === 'enter' || this.queue.length >= MAX_BATCH) this.flush(false);
    else this.schedule();
  }

  private schedule(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => this.flush(false), FLUSH_MS);
  }

  private flush(leaving: boolean): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    while (this.queue.length) {
      const events = this.queue.splice(0, MAX_BATCH);
      void fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId: this.visitorId(), events }),
        credentials: 'include',
        keepalive: leaving
      }).catch(() => undefined);
    }
  }
}
